import { useLocalizedNavigate } from '../../../hooks/useLocalizedNavigate';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { useNotifications } from '../../../hooks/notifications/useNotifications';
import { NotificationStatus, NotificationTypes } from '../../../utils/types';
import { Title, TextField, UserAvatar, Icon } from '../../common';
import { readNotifications } from '../../../store/notification/notificationSlice';
import { useTranslation } from '../../../hooks/translations/TranslationProvider';
import { formatDistanceWithLocale } from '../../../utils/helpers';
import NotificationContent from './NotificationContent';

import './notifications.css';

const Notifications = () => {
    const navigate = useLocalizedNavigate();
    const { t } = useTranslation();
    const { notifications } = useNotifications();
    const dispatch = useAppDispatch();
    const auth = useAppSelector(state => state.auth);

    const [showMenu, setShowMenu] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setShowMenu(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const showMenuHandler = async (event: React.MouseEvent<SVGSVGElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (
            notifications.some(
                notification =>
                    notification.status === NotificationStatus.UNREAD
            )
        ) {
            await dispatch(readNotifications());
        }

        setShowMenu(true);
    };

    const onNotificationClick = (
        type: NotificationTypes,
        senderId: string,
        eventId?: string
    ) => {
        if (type === NotificationTypes.FOLLOW) {
            navigate(`/users/${senderId}`);
            setShowMenu(false);
        } else {
            navigate(`/events/${eventId}`);
            setShowMenu(false);
        }
    };

    const showNotificationDot = useMemo(
        () =>
            notifications.some(
                notification =>
                    notification.status === NotificationStatus.UNREAD
            ),
        [notifications]
    );

    return (
        <div className="notifications" ref={ref}>
            <Icon
                name="bx-bell"
                onClick={showMenuHandler}
                size="23px"
                cursor="pointer"
            />
            {showNotificationDot && <div className="new-notifications-dot" />}

            {showMenu && (
                <ul className="notifications-dropdown-menu">
                    <li className="notifications-title">
                        <Title text={t('notifications.title')} size="s" />
                    </li>
                    {notifications.length > 0 ? (
                        notifications.map(notification => (
                            <li
                                className="notifications-menu-element"
                                key={notification.id}
                                onClick={() =>
                                    onNotificationClick(
                                        notification.type,
                                        notification.sender.id,
                                        notification.sportEvent?.id
                                    )
                                }
                            >
                                <UserAvatar
                                    imageSrc={notification.sender.avatarUrl}
                                    size="m"
                                    averageRating={
                                        notification.sender.averageRating
                                    }
                                />
                                <div>
                                    <NotificationContent
                                        notification={notification}
                                    />
                                    <TextField
                                        size="xs"
                                        color="green"
                                    >{`${formatDistanceWithLocale(
                                        notification.date,
                                        auth.authData?.language!
                                    )} ago`}</TextField>
                                </div>
                            </li>
                        ))
                    ) : (
                        <li className="notifications-menu-element">
                            <TextField size="s">
                                {t('notifications.empty')}
                            </TextField>
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
};

export default Notifications;
